import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Printer, Receipt } from 'lucide-react';
import api from '../../../services/api';
import { getManifest, paymentMethodLabel } from '../../../services/bookings';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import Card from '../../../components/shared/Card/Card';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Button from '../../../components/shared/Button/Button';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import LoadingSpinner from '../../../components/shared/LoadingSpinner/LoadingSpinner';
import type { Trip, ManifestResponse, ManifestRow, ManifestSummary } from '../../../types/api';
import { seatLabel } from '../../../utils/seatLabel';
import BookingHistoryModal from './BookingHistoryModal';
import AddExpenseModal from './AddExpenseModal';

function humaniseCategory(cat: string): string {
  return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// The trip detail endpoint returns nested route/vehicle/driver objects.
interface TripDetail extends Omit<Trip, 'route' | 'vehicle' | 'driver'> {
  route: { id: number; origin: string; destination: string } | null;
  vehicle: { id: number; registration_number: string } | null;
  driver: { id: number; full_name: string } | null;
}

const Manifest: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const [trip, setTrip] = useState<TripDetail | null>(null);
  const [manifest, setManifest] = useState<ManifestResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState<ManifestRow | null>(null);
  const [historyId, setHistoryId] = useState<number | null>(null);
  const [expenseOpen, setExpenseOpen] = useState(false);

  const load = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    try {
      const [tripRes, manifestData] = await Promise.all([
        api.get<TripDetail>(`/trips/${tripId}/`).catch(() => ({ data: null })),
        getManifest(Number(tripId)).catch(() => null),
      ]);
      setTrip(tripRes.data);
      setManifest(manifestData);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { load(); }, [load]);

  const markPaidMutation = useResourceMutation(
    async (id: number) => { await api.post(`/bookings/${id}/mark_paid/`); },
    { successMessage: 'Marked paid' }
  );
  const cancelMutation = useResourceMutation(
    async (id: number) => { await api.post(`/bookings/${id}/cancel/`); },
    { successMessage: 'Booking cancelled' }
  );
  const resendTicketMutation = useResourceMutation(
    async (id: number) => { await api.post(`/bookings/${id}/resend_ticket/`); },
    { successMessage: 'Ticket SMS re-queued' }
  );

  const handleMarkPaid = async (item: ManifestRow) => {
    try { await markPaidMutation.run(item.id); load(); } catch { /* toast handled by hook */ }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try { await cancelMutation.run(cancelTarget.id); setCancelTarget(null); load(); } catch { /* toast */ }
  };

  if (loading) return <LoadingSpinner centered />;

  const bookings = manifest?.bookings ?? [];
  const paidCount = bookings.filter((m) => m.payment_status === 'paid').length;
  const totalFare = manifest?.bookings.reduce((sum, b) => sum + parseFloat(b.segment_fare || '0'), 0) ?? 0;

  const routeLabel = trip?.route
    ? `${trip.route.origin} → ${trip.route.destination}`
    : `Trip #${tripId}`;

  const columns: Column<ManifestRow>[] = [
    { key: 'seat_number',        header: 'Seat',
      render: (m) => <span className="font-bold">#{m.seat_label ?? seatLabel(m.seat_number)}</span> },
    { key: 'booking_reference',  header: 'Reference',
      render: (m) => <code className="text-xs">{m.booking_reference}</code> },
    { key: 'passenger_name',     header: 'Passenger',
      render: (m) => (
        <div className="flex flex-col gap-0.5">
          <span>{m.passenger_name}</span>
          {m.transferred_in && (
            <span className="text-xs text-amber-700">
              Transferred from {m.transferred_in.from_trip_route || `Trip #${m.transferred_in.from_trip_id}`}
              {' '}seat #{m.transferred_in.from_seat_label ?? seatLabel(m.transferred_in.from_seat_number)}
              {' '}by {m.transferred_in.transferred_by}
              {' '}on {new Date(m.transferred_in.transferred_at).toLocaleDateString([], { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>
      ) },
    { key: 'booked_by_name',     header: 'Booked by',
      render: (m) => (
        <div>
          <div>{m.booked_by_name}</div>
          {m.last_edited_by_name && (
            <div className="text-xs text-slate-500">Last edited by {m.last_edited_by_name}</div>
          )}
        </div>
      ) },
    { key: 'passenger_phone',    header: 'Phone' },
    { key: 'passenger_id_number', header: 'ID Number',
      render: (m) => m.passenger_id_number || '—' },
    { key: 'boarding_stop_name', header: 'Boarding',
      render: (m) => m.boarding_stop_name ?? '—' },
    { key: 'alighting_stop_name', header: 'Alighting',
      render: (m) => m.alighting_stop_name ?? '—' },
    { key: 'segment_fare',       header: 'Fare',
      render: (m) => `KES ${m.segment_fare}` },
    { key: 'boarding_eta',       header: 'Boarding ETA',
      render: (m) => m.boarding_eta
        ? new Date(m.boarding_eta).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '—' },
    { key: 'payment_method',     header: 'Method',
      render: (m) => paymentMethodLabel(m.payment_method) },
    { key: 'payment_status',     header: 'Payment',
      render: (m) => <StatusBadge variant="booking" status={m.payment_status} /> },
  ];

  const openReceipt = async (m: ManifestRow) => {
    // Fetch as blob so the auth Bearer header goes with the request (a plain
    // window.open would hit the endpoint anonymously and 401). Then wrap in a
    // blob URL so the new tab can render the PDF and the operator prints via
    // the OS dialog.
    try {
      const response = await api.get(`/bookings/${m.id}/receipt/`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data as Blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      console.error('receipt download failed', e);
    }
  };

  const handleResendTicket = async (m: ManifestRow) => {
    try { await resendTicketMutation.run(m.id); } catch { /* toast */ }
  };

  const rowActions = (m: ManifestRow) => {
    const historyButton = (
      <Button variant="secondary" size="sm" onClick={() => setHistoryId(m.id)}>
        History
      </Button>
    );
    if (m.payment_status === 'paid') {
      return (
        <div className="inline-flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => openReceipt(m)}>
            Print receipt
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleResendTicket(m)}
            loading={resendTicketMutation.loading}
          >
            Send ticket
          </Button>
          {historyButton}
        </div>
      );
    }
    if (m.payment_status !== 'pending') return historyButton;
    return (
      <div className="inline-flex items-center gap-2">
        <Button size="sm" onClick={() => handleMarkPaid(m)} loading={markPaidMutation.loading}>
          Mark paid
        </Button>
        <Button variant="danger" size="sm" onClick={() => setCancelTarget(m)}>
          Cancel
        </Button>
        {historyButton}
      </div>
    );
  };

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link
            to="/admin/operations/trips"
            className="inline-flex items-center gap-1.5 hover:text-brand-700"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Back to trips
          </Link>
        }
        title={`Manifest — ${routeLabel}`}
        description={[
          trip?.vehicle?.registration_number ? `Vehicle ${trip.vehicle.registration_number}` : null,
          trip?.driver?.full_name ? `Driver ${trip.driver.full_name}` : null,
        ].filter(Boolean).join(' · ') || undefined}
        actions={
          <div className="inline-flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setExpenseOpen(true)}
              icon={<Receipt className="h-4 w-4" />}
            >
              Expense
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.print()}
              icon={<Printer className="h-4 w-4" />}
            >
              Print
            </Button>
          </div>
        }
      />

      {manifest && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
          <div className="font-semibold text-slate-700">Route</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-slate-600">
            {manifest.route_stops.map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 && <span className="text-slate-400">→</span>}
                <span>{s.name}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {trip && (
        <Card title="Trip details" className="mb-6">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Route</dt>
              <dd className="mt-1 text-base font-medium text-slate-900">{routeLabel}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vehicle</dt>
              <dd className="mt-1 text-base font-medium text-slate-900">{trip.vehicle?.registration_number ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Driver</dt>
              <dd className="mt-1 text-base font-medium text-slate-900">{trip.driver?.full_name ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Departure</dt>
              <dd className="mt-1 text-base font-medium text-slate-900">{new Date(trip.departure_time).toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Fare</dt>
              <dd className="mt-1 text-base font-bold tabular-nums text-brand-700">KES {totalFare.toFixed(2)}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</dt>
              <dd className="mt-1.5"><StatusBadge variant="trip" status={trip.status} /></dd>
            </div>
          </dl>
        </Card>
      )}

      {manifest?.summary && (() => {
        const summary: ManifestSummary = manifest.summary;
        return (
          <Card title="Financial summary" className="mb-6">
            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Income (paid)</dt>
                <dd className="mt-1 text-base font-medium tabular-nums text-slate-900">KES {summary.income}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Commission</dt>
                <dd className="mt-1 text-base font-medium tabular-nums text-slate-900">KES {summary.commission}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Net</dt>
                <dd className="mt-1 text-base font-bold tabular-nums text-brand-700">KES {summary.net}</dd>
              </div>
              <div className="sm:col-span-3">
                <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Expenses</dt>
                {summary.expenses.length === 0 ? (
                  <dd className="mt-1 text-sm text-slate-400">No expenses recorded</dd>
                ) : (
                  <dd className="mt-1">
                    <ul className="space-y-1">
                      {summary.expenses.map((e, i) => (
                        <li key={i} className="flex items-center justify-between text-sm text-slate-700">
                          <span>{humaniseCategory(e.category)}{e.description ? ` — ${e.description}` : ''}</span>
                          <span className="tabular-nums">KES {e.amount}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-right text-xs text-slate-500">Total: KES {summary.expenses_total}</p>
                  </dd>
                )}
              </div>
            </dl>
          </Card>
        );
      })()}

      <Card
        title={`Passenger manifest (${bookings.length})`}
        description={`${paidCount} paid · ${bookings.length - paidCount} unpaid`}
        bodyClassName="!p-0"
      >
        <div className="px-5 py-4">
          <DataTable<ManifestRow>
            columns={columns}
            data={bookings}
            rowKey="id"
            emptyTitle="No passengers booked"
            emptyDescription="Bookings on this trip will appear here as they come in."
            actions={rowActions}
            pageSize={50}
          />
        </div>
      </Card>

      {manifest && manifest.transfers_out.length > 0 && (
        <Card
          title={`Outbound transfers (${manifest.transfers_out.length})`}
          description="Bookings that used to sit on this trip but were moved elsewhere."
          className="mt-6"
        >
          <ul className="divide-y divide-slate-100">
            {manifest.transfers_out.map((t) => (
              <li key={`${t.booking_reference}-${t.transferred_at}`} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-medium text-slate-900">
                    {t.passenger_name}{' '}
                    <code className="ml-1 text-xs text-slate-500">{t.booking_reference}</code>
                  </div>
                  <div className="text-sm text-slate-600">
                    Seat #{t.from_seat_label ?? seatLabel(t.from_seat_number)} → {t.to_trip_route || `Trip #${t.to_trip_id}`}
                    {' '}seat #{t.to_seat_label ?? seatLabel(t.to_seat_number)}
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  by {t.transferred_by} on {new Date(t.transferred_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {manifest && manifest.cancellations.length > 0 && (
        <Card
          title={`Cancellations (${manifest.cancellations.length})`}
          description="Bookings that were cancelled on this trip. A seat may already be re-booked above."
          className="mt-6"
        >
          <ul className="divide-y divide-slate-100">
            {manifest.cancellations.map((c) => (
              <li key={`${c.booking_reference}-${c.cancelled_at}`} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-medium text-slate-900">
                    {c.passenger_name}{' '}
                    <code className="ml-1 text-xs text-slate-500">{c.booking_reference}</code>
                  </div>
                  <div className="text-sm text-slate-600">
                    Seat #{c.seat_label ?? seatLabel(c.seat_number)}{c.passenger_phone ? ` · ${c.passenger_phone}` : ''}
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  cancelled by {c.cancelled_by} on {new Date(c.cancelled_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ConfirmDialog
        isOpen={!!cancelTarget}
        title="Cancel booking?"
        message={`Cancel booking ${cancelTarget?.booking_reference ?? ''}? This will free the seat and cannot be undone.`}
        confirmLabel="Cancel booking"
        variant="danger"
        loading={cancelMutation.loading}
        onConfirm={handleCancel}
        onCancel={() => setCancelTarget(null)}
      />
      <BookingHistoryModal bookingId={historyId} onClose={() => setHistoryId(null)} />
      {tripId && (
        <AddExpenseModal
          isOpen={expenseOpen}
          tripId={Number(tripId)}
          tripLabel={[
            routeLabel,
            trip?.vehicle?.registration_number ? `Vehicle ${trip.vehicle.registration_number}` : null,
          ].filter(Boolean).join(' · ')}
          onClose={() => setExpenseOpen(false)}
        />
      )}
    </>
  );
};

export default Manifest;
